// Select Component
// Dropdown select component with TypeScript support

import { forwardRef, ChangeEvent, FocusEvent, SelectHTMLAttributes } from 'react';

import styles from './Select.module.css';

interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'onBlur'> {
    label?: string;
    name?: string;
    value?: string;
    options?: SelectOption[];
    placeholder?: string;
    error?: string;
    helperText?: string;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
    onBlur?: (e: FocusEvent<HTMLSelectElement>) => void;
    className?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(({
    label,
    name,
    value,
    options = [],
    placeholder = 'Select an option',
    error,
    helperText,
    required = false,
    disabled = false,
    fullWidth = false,
    onChange,
    onBlur,
    className = '',
    ...props
}, ref) => {
    const selectClasses = [
        styles.select,
        error && styles.error,
        disabled && styles.disabled,
        fullWidth && styles.fullWidth,
        className
    ].filter(Boolean).join(' ');

    return (
        <div className={`${styles.selectWrapper} ${fullWidth ? styles.fullWidth : ''}`}>
            {label && (
                <label htmlFor={name} className={styles.label}>
                    {label}
                    {required && <span className={styles.required}>*</span>}
                </label>
            )}
            <select
                ref={ref}
                id={name}
                name={name}
                value={value}
                disabled={disabled}
                onChange={onChange}
                onBlur={onBlur}
                className={selectClasses}
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? `${name}-error` : helperText ? `${name}-helper` : undefined}
                {...props}
            >
                <option value="" disabled>
                    {placeholder}
                </option>
                {options.map((option, index) => (
                    <option
                        key={option.value ? option.value : `option-${index}`}
                        value={option.value}
                        disabled={option.disabled}
                    >
                        {option.label}
                    </option>
                ))}
            </select>
            {error && (
                <span id={`${name}-error`} className={styles.errorText}>
                    {error}
                </span>
            )}
            {helperText && !error && (
                <span id={`${name}-helper`} className={styles.helperText}>
                    {helperText}
                </span>
            )}
        </div>
    );
});

Select.displayName = 'Select';

export default Select;
